import * as React from 'react';
import {
  TextField,
  TextFieldProps,
  IconButton,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import EditLocationIcon from '@mui/icons-material/EditLocation';
import { Controller, useFormContext } from 'react-hook-form';
import { formatAddress, parseAddress } from '../../utils/addressFormat';

const INDIRIZZO_TIPI = [
  'Via',
  'Piazza',
  'Strada',
  'Largo',
  'Viale',
  'Corso',
  'Piazzale',
  'Piazzetta',
  'Vicolo',
  'Vico',
  'Rampa',
  'Salita',
  'Discesa',
  'Passeggiata',
  'Lungomare',
  'Lungolago',
  'Rotonda',
  'Piazzale',
  'Piazzetta',
  'Cortile',
  'Piano',
  'Scalinata',
];

interface AddressFields {
  tipoIndirizzo: string;
  via: string;
  numero: string;
  citta: string;
  cap: string;
  provincia: string;
}

export interface InputAddressProps extends Omit<TextFieldProps, 'value' | 'onChange'> {
  name: string;
  label: string;
  required?: boolean;
  /** Chiamato al blur del campo e alla conferma del dialogo indirizzo. */
  onAddressCommitted?: (address: string) => void;
}

export const InputAddress: React.FC<InputAddressProps> = ({
  name,
  label,
  required = false,
  onAddressCommitted,
  ...textFieldProps
}) => {
  const {
    control,
    formState: { errors },
    setValue,
    watch,
  } = useFormContext();
  const [open, setOpen] = React.useState(false);
  const [addressFields, setAddressFields] = React.useState<AddressFields>({
    tipoIndirizzo: '',
    via: '',
    numero: '',
    citta: '',
    cap: '',
    provincia: '',
  });

  const currentValue = watch(name);

  // Parse existing address when opening modal
  React.useEffect(() => {
    if (open && currentValue) {
      let tipoIndirizzo = '';
      let remainingAddress = String(currentValue).trim();

      // Try to extract tipo indirizzo (Via, Piazza, Strada, etc.) from the beginning
      for (const tipo of INDIRIZZO_TIPI) {
        const tipoRegex = new RegExp(`^${tipo}\\s+`, 'i');
        if (tipoRegex.test(remainingAddress)) {
          tipoIndirizzo = tipo;
          remainingAddress = remainingAddress.replace(tipoRegex, '');
          break;
        }
      }

      // Formato: "{via} {numero}, {cap}, {citta} ({Provincia})" (accetta anche il formato legacy
      // "{via} {numero}, {citta}, {cap} ({Provincia})")
      const parsed = parseAddress(remainingAddress);

      setAddressFields({
        tipoIndirizzo: tipoIndirizzo || 'Via',
        via: parsed.via,
        numero: parsed.numero,
        citta: parsed.citta,
        cap: parsed.cap,
        provincia: parsed.provincia,
      });
    } else if (open && !currentValue) {
      // Reset fields when opening modal with empty address
      setAddressFields({
        tipoIndirizzo: 'Via',
        via: '',
        numero: '',
        citta: '',
        cap: '',
        provincia: '',
      });
    }
  }, [open, currentValue]);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSave = () => {
    // Compose address: "{TIPO} {via} {numero}, {cap}, {citta} ({Provincia})"
    const fullAddress = formatAddress({
      via: addressFields.via ? `${addressFields.tipoIndirizzo} ${addressFields.via}`.trim() : '',
      numero: addressFields.numero,
      cap: addressFields.cap,
      citta: addressFields.citta,
      provincia: addressFields.provincia,
    });

    if (fullAddress) {
      setValue(name, fullAddress, { shouldDirty: true, shouldValidate: true });
      onAddressCommitted?.(fullAddress);
    }

    handleClose();
  };

  return (
    <>
      <Controller
        name={name}
        control={control}
        rules={{ required: required ? 'Questo campo è obbligatorio' : false }}
        render={({ field }) => (
          <TextField
            {...field}
            {...textFieldProps}
            fullWidth
            label={label}
            required={required}
            error={!!errors[name]}
            helperText={errors[name]?.message as string}
            variant="outlined"
            placeholder="Via Numero, Cap, Città (Provincia)"
            onBlur={event => {
              field.onBlur();
              const value = event.target.value.trim();
              if (value) {
                onAddressCommitted?.(value);
              }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={{ pr: '4px' }}>
                  <IconButton
                    onClick={handleOpen}
                    edge="end"
                    aria-label="Modifica indirizzo"
                    sx={{ color: 'text.secondary' }}
                  >
                    <EditLocationIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        )}
      />

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Inserisci Indirizzo</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              {/* Tipo indirizzo, Via e Numero */}
              <Grid size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth>
                  <InputLabel>Indicazioni</InputLabel>
                  <Select
                    value={addressFields.tipoIndirizzo}
                    label="Indicazioni"
                    onChange={e =>
                      setAddressFields(prev => ({ ...prev, tipoIndirizzo: e.target.value }))
                    }
                  >
                    <MenuItem value="">
                      <em>Nessuna</em>
                    </MenuItem>
                    {INDIRIZZO_TIPI.map(tipo => (
                      <MenuItem key={tipo} value={tipo}>
                        {tipo}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 5 }}>
                <TextField
                  fullWidth
                  label="Indirizzo"
                  value={addressFields.via}
                  onChange={e => setAddressFields(prev => ({ ...prev, via: e.target.value }))}
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <TextField
                  fullWidth
                  label="Numero"
                  value={addressFields.numero}
                  onChange={e => setAddressFields(prev => ({ ...prev, numero: e.target.value }))}
                  variant="outlined"
                />
              </Grid>

              {/* Città */}
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Città"
                  value={addressFields.citta}
                  onChange={e => setAddressFields(prev => ({ ...prev, citta: e.target.value }))}
                  variant="outlined"
                />
              </Grid>

              {/* CAP e Provincia sulla stessa riga */}
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="CAP"
                  value={addressFields.cap}
                  onChange={e => setAddressFields(prev => ({ ...prev, cap: e.target.value }))}
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 8 }}>
                <TextField
                  fullWidth
                  label="Provincia"
                  value={addressFields.provincia}
                  onChange={e => setAddressFields(prev => ({ ...prev, provincia: e.target.value }))}
                  variant="outlined"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Box sx={{ pb: 2, pr: 2 }}>
            <Button onClick={handleClose}>Annulla</Button>
            <Button sx={{ ml: 2 }} onClick={handleSave} variant="contained" color="primary">
              Conferma
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};
